import { supabase } from './supabase';
import { Devotion } from '../types';

export async function getDevotions(): Promise<Devotion[]> {
  try {
    console.group('Fetching Devotions');
    console.time('Fetch Time');

    const { data, error } = await supabase
      .from('devotions')
      .select('*')
      .order('day');

    if (error) throw error;

    console.log('Database Fetch Results:', {
      count: data?.length || 0,
      dayRange: data?.length ? {
        min: Math.min(...data.map(d => d.day)),
        max: Math.max(...data.map(d => d.day))
      } : null,
      sections: data?.length ? 
        Array.from(new Set(data.map(d => d.section))) : []
    });

    const devotions = data.map(row => ({
      id: row.id,
      title: row.title,
      authorQuote: {
        text: row.author_quote_text || '',
        author: row.author_quote_author || ''
      },
      theme: row.theme,
      description: row.description,
      verse: row.verse,
      parable: {
        title: row.parable_title || '',
        content: row.parable_content || ''
      },
      reflection: row.reflection || [],
      actionSteps: row.action_steps || [],
      prayer: row.prayer,
      section: row.section,
      day: row.day
    }));

    console.timeEnd('Fetch Time');
    console.groupEnd();

    return devotions;
  } catch (error) {
    console.error('Error fetching devotions:', error);
    console.groupEnd();
    throw error;
  }
}

export async function importDevotions(devotions: Devotion[]): Promise<void> {
  try {
    console.group('Importing Devotions');
    console.time('Import Time');

    // First, check if we have admin access
    const { data: roleData, error: roleError } = await supabase
      .from('user_roles')
      .select('role')
      .single();

    if (roleError || roleData?.role !== 'admin') {
      throw new Error('Admin access required to import devotions');
    }

    // Transform devotions for database format
    const dbDevotions = devotions.map(devotion => ({
      id: devotion.day, // Use day as ID for consistency
      title: devotion.title,
      author_quote_text: devotion.authorQuote.text,
      author_quote_author: devotion.authorQuote.author,
      theme: devotion.theme,
      description: devotion.description,
      verse: devotion.verse,
      parable_title: devotion.parable.title,
      parable_content: devotion.parable.content,
      reflection: devotion.reflection,
      action_steps: devotion.actionSteps,
      prayer: devotion.prayer,
      section: devotion.section,
      day: devotion.day
    }));

    console.log('Processing Import:', {
      totalDevotions: dbDevotions.length,
      dayRange: {
        min: Math.min(...dbDevotions.map(d => d.day)),
        max: Math.max(...dbDevotions.map(d => d.day))
      },
      sections: Array.from(new Set(dbDevotions.map(d => d.section)))
    });

    // Process in batches of 50
    const batchSize = 50;
    for (let i = 0; i < dbDevotions.length; i += batchSize) {
      const batch = dbDevotions.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(dbDevotions.length / batchSize);

      console.log(`Processing batch ${batchNumber}/${totalBatches}`, {
        size: batch.length,
        firstDay: batch[0].day,
        lastDay: batch[batch.length - 1].day
      });

      const { error: upsertError } = await supabase
        .from('devotions')
        .upsert(batch, {
          onConflict: 'day',
          ignoreDuplicates: false
        });

      if (upsertError) {
        console.error(`Batch ${batchNumber} failed:`, upsertError);
        throw new Error(`Failed to import batch ${batchNumber}: ${upsertError.message}`);
      }

      console.log(`Completed batch ${batchNumber}/${totalBatches}`);
    }

    // Verify the import
    const { count, error: countError } = await supabase
      .from('devotions')
      .select('*', { count: 'exact', head: true });

    if (countError) {
      throw new Error('Failed to verify import: ' + countError.message);
    }

    console.log('Import Verification:', {
      expectedCount: dbDevotions.length,
      actualCount: count
    });

    console.timeEnd('Import Time');
    console.groupEnd();
  } catch (error) {
    console.error('Import failed:', error);
    console.groupEnd();
    throw error;
  }
}